import React, { useState } from "react";
import { Alert, Button, Dropdown, Typography } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { ContractStage, WbSubject } from "../../workbench/types/WbTypes";
import { contractStages } from "../utils/contractStages";

type Props = {
    subject: WbSubject;
    isSaving: boolean;
    needsRefresh: boolean;
    error: string | null;
    onChange: (stage: ContractStage) => void;
    onRefresh: () => void;
};

const WorkbenchNewSubjectTopPanel: React.FC<Props> = ({
    subject,
    isSaving,
    needsRefresh,
    error,
    onChange,
    onRefresh,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragTarget, setDragTarget] = useState<ContractStage | null>(null);
    const current = contractStages.find(
        (stage) => stage.name === (subject.contractStage ?? "Intake"),
    );
    const index = current ? contractStages.indexOf(current) : -1;
    const disabled = isSaving || needsRefresh;
    const dragValue = `${subject.id}:${current?.name}`;
    const style = {
        "--contract-stage-color": current?.color ?? "#64748b",
        "--contract-stage-halo": current?.halo ?? "transparent",
        "--contract-stage-progress": `${(Math.max(index, 0) * 100) / 6}%`,
    } as React.CSSProperties;

    return (
        <div
            className="workbench-new-subject-top-panel"
            style={style}
            aria-busy={isSaving}
        >
            <div className="workbench-new-subject-stage-panel">
                <div className="workbench-new-subject-stage-copy">
                    <Typography.Text type="secondary">
                        Current stage
                    </Typography.Text>
                    <div
                        className="workbench-new-subject-stage-title"
                        aria-live="polite"
                    >
                        <span className="workbench-new-subject-stage-dot" />
                        {current?.name ?? "Not set"}
                    </div>
                    <p>
                        {current?.description ??
                            "Select a stage to track this Contract."}
                    </p>
                    <Dropdown
                        trigger={["click"]}
                        disabled={disabled}
                        menu={{
                            selectedKeys: current ? [current.name] : [],
                            items: contractStages.map((stage) => ({
                                key: stage.name,
                                label: stage.name,
                            })),
                            onClick: ({ key }) => {
                                const target = contractStages.find(
                                    (stage) => stage.name === key,
                                );
                                if (target) onChange(target.name);
                            },
                        }}
                    >
                        <Button
                            loading={isSaving}
                            disabled={disabled}
                            className="workbench-new-subject-change-stage"
                        >
                            Change stage <DownOutlined />
                        </Button>
                    </Dropdown>
                </div>
                <div className="workbench-new-subject-stage-scroll">
                    <div
                        className="workbench-new-subject-stage-track"
                        aria-label="Contract stages"
                    >
                        {contractStages.map((stage) => {
                            const active = stage === current;
                            return (
                                <Button
                                    type="text"
                                    className={`workbench-new-subject-stage-step${active ? " active" : ""}${isDragging && !disabled && dragTarget === stage.name ? " drag-target" : ""}`}
                                    key={stage.name}
                                    aria-current={active ? "step" : undefined}
                                    aria-label={`${stage.name} stage${active ? ", current; drag to change" : ""}`}
                                    disabled={disabled}
                                    draggable={active && !disabled}
                                    onClick={() => onChange(stage.name)}
                                    onDragStart={(event) => {
                                        if (!active || disabled) {
                                            event.preventDefault();
                                            return;
                                        }
                                        event.dataTransfer.effectAllowed =
                                            "move";
                                        event.dataTransfer.setData(
                                            "text/plain",
                                            dragValue,
                                        );
                                        setIsDragging(true);
                                    }}
                                    onDragEnd={() => {
                                        setIsDragging(false);
                                        setDragTarget(null);
                                    }}
                                    onDragOver={(event) => {
                                        if (isDragging && !disabled) {
                                            event.preventDefault();
                                            event.dataTransfer.dropEffect =
                                                "move";
                                            setDragTarget(stage.name);
                                        }
                                    }}
                                    onDragLeave={(event) => {
                                        if (
                                            !event.currentTarget.contains(
                                                event.relatedTarget as Node | null,
                                            )
                                        )
                                            setDragTarget(null);
                                    }}
                                    onDrop={(event) => {
                                        event.preventDefault();
                                        setIsDragging(false);
                                        setDragTarget(null);
                                        if (
                                            !disabled &&
                                            event.dataTransfer.getData(
                                                "text/plain",
                                            ) === dragValue
                                        )
                                            onChange(stage.name);
                                    }}
                                >
                                    <span className="workbench-new-subject-stage-node" />
                                    <span>{stage.name}</span>
                                </Button>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className="workbench-new-subject-readiness">
                <div className="workbench-new-subject-readiness-heading">
                    <strong>Stage guide</strong>
                </div>
                <div className="workbench-new-subject-readiness-subtitle">
                    Suggested items
                </div>
                {current ? (
                    <ul>
                        {current.items.map((item) => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                ) : (
                    <p>Select a stage to see suggested items</p>
                )}
            </div>
            {error ? (
                <Alert
                    className="workbench-new-subject-stage-error"
                    type="warning"
                    showIcon
                    message={error}
                    action={
                        needsRefresh ? (
                            <Button loading={isSaving} onClick={onRefresh}>
                                Retry loading
                            </Button>
                        ) : undefined
                    }
                />
            ) : null}
        </div>
    );
};

export default WorkbenchNewSubjectTopPanel;
